import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Tooltip, Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { documentsApi, isCancelled } from './api';
import { PERM_HINTS, statusColor } from './constants';
import { apiBlobError, apiError, downloadBlob } from './format';
import { MarkingChip, WatchBadge } from './DocumentsTable';
import { CardResponse, DocumentsAccess, Export1cSide, ExportFormat, ExportKind } from './types';
import { BORDER, CopyButton, DANGER, DARK, SOFT } from './card/cardKit';
import CardSidebar from './card/CardSidebar';
import CardItemsPanel from './card/CardItemsPanel';
import CardConfirmDialog, { ConfirmKind } from './card/CardConfirmDialog';
import { notify } from '../../notifications';

interface Props {
  id: number | null;
  access: DocumentsAccess;
  onClose: () => void;
  /** Вызывается после изменения документа (повторная обработка, продление) - список нужно обновить */
  onChanged: () => void;
}

const MENU_SX = { border: `1px solid ${BORDER}`, boxShadow: '0 8px 24px rgba(15,23,42,0.10)' } as const;

/** Карточка документа: загрузка, шапка с действиями, левая панель (реквизиты, история, действия в портале), товары */
const DocumentCardDialog: React.FC<Props> = ({ id, access, onClose, onChanged }) => {
  const [data, setData] = useState<CardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [side, setSide] = useState<Export1cSide>('receiver');
  const [deleteAck, setDeleteAck] = useState(false);

  /** Открытие карточки пишется в журнал действий; перезагрузка после действия - silent */
  const load = useCallback((docId: number, silent: boolean, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    return documentsApi.card(docId, silent, signal)
      .then(res => setData(res))
      .catch(err => {
        if (isCancelled(err)) return;
        setError(apiError(err, 'Не удалось загрузить документ'));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (id === null) return undefined;
    setData(null);
    const controller = new AbortController();
    load(id, false, controller.signal);
    return () => controller.abort();
  }, [id, load]);

  const doc = data?.document || null;

  /** Выгрузка КМ / позиций собирается на сервере: там же проверяются права и зона видимости */
  const exportDocument = async (kind: ExportKind, format: ExportFormat) => {
    if (!doc) return;
    setExportAnchor(null);
    try {
      const blob = await documentsApi.exportDocument(doc.id, kind, format);
      downloadBlob(`document_${doc.id}_${kind}.${format}`, blob);
    } catch (err) {
      notify.error(await apiBlobError(err, 'Не удалось выгрузить данные документа'));
    }
  };

  const openConfirm = (kind: ConfirmKind) => {
    setActionsAnchor(null);
    setDeleteAck(false);
    if (kind === 'export1c' && doc) setSide(doc.export1cSides[0] || 'receiver');
    setConfirm(kind);
  };

  const openRetry = useCallback(() => setConfirm('retry'), []);
  const cancelConfirm = useCallback(() => setConfirm(null), []);

  const runAction = async () => {
    if (!doc || !confirm) return;
    setBusy(true);
    try {
      let res: { message?: string };
      switch (confirm) {
        case 'retry': res = await documentsApi.retry(doc.id); break;
        case 'extend': res = await documentsApi.extend([doc.id]); break;
        case 'hide': res = await documentsApi.hide(doc.id, true); break;
        case 'unhide': res = await documentsApi.hide(doc.id, false); break;
        case 'reject': res = await documentsApi.reject(doc.id); break;
        case 'delete': res = await documentsApi.deleteDraft(doc.id); break;
        default: res = await documentsApi.export1c(doc.id, side);
      }
      notify.success(res.message || 'Готово');
      setConfirm(null);
      onChanged();
      if (confirm === 'delete') {
        // черновика больше нет - закрываем карточку, перезагружать нечего
        setData(null);
        onClose();
        return;
      }
      await load(doc.id, true);
    } catch (err) {
      notify.error(apiError(err, 'Операция не выполнена'));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  /** Без права элементы не рендерятся; если право есть, но документ вне зоны или статус не подходит - кнопка неактивна с подсказкой */
  const retryVisible = Boolean(doc && doc.status === 14 && access.retryScope);
  const extendVisible = Boolean(doc && access.warningsExtend && (doc.canExtend || (doc.watch && doc.watch.level !== 'ok')));

  /** Действия над документом: только те, на которые у оператора есть право и которые применимы к статусу */
  const actionItems: Array<{ kind: ConfirmKind; label: string; icon: React.ReactNode; enabled: boolean; hint: string; danger?: boolean }> = doc ? [
    ...(access.hideScope ? [doc.isHidden
      ? { kind: 'unhide' as const, label: 'Вернуть документ', icon: <VisibilityOutlinedIcon fontSize="small" />, enabled: doc.canUnhide, hint: PERM_HINTS.hideOutOfZone }
      : { kind: 'hide' as const, label: 'Скрыть документ', icon: <VisibilityOffOutlinedIcon fontSize="small" />, enabled: doc.canHide, hint: PERM_HINTS.hideOutOfZone }] : []),
    ...(doc.status === 15 && access.rejectScope ? [{ kind: 'reject' as const, label: 'Отклонить документ', icon: <BlockRoundedIcon fontSize="small" />, enabled: doc.canReject, hint: PERM_HINTS.rejectOutOfZone }] : []),
    ...(doc.status === 9 && access.deleteDraftScope ? [{ kind: 'delete' as const, label: 'Удалить черновик', icon: <DeleteOutlineRoundedIcon fontSize="small" />, enabled: doc.canDeleteDraft, hint: PERM_HINTS.deleteDraftOutOfZone, danger: true }] : []),
    ...(access.export1cScope ? [{ kind: 'export1c' as const, label: 'Выгрузить накладную в 1С', icon: <CloudUploadOutlinedIcon fontSize="small" />, enabled: doc.export1cSides.length > 0, hint: PERM_HINTS.export1cOutOfZone }] : [])
  ] : [];

  const canExportMarks = access.exportMarks && access.viewMarks;
  const stColor = doc ? statusColor(doc.status) : '#94a3b8';
  const btnSx = { textTransform: 'none', fontSize: '0.82rem', borderRadius: 1.5 } as const;

  return (
    <>
      <Dialog
        open={id !== null} onClose={onClose} maxWidth="xl" fullWidth
        PaperProps={{ sx: { height: '90vh', bgcolor: SOFT, borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Шапка */}
        <Paper elevation={0} sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, bgcolor: '#fff', flexShrink: 0, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>ДОКУМЕНТ</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: DARK, lineHeight: 1 }}>#{id}</Typography>
                <CopyButton text={id} />
              </Box>
            </Box>

            {doc && (
              <>
                <Divider orientation="vertical" flexItem sx={{ height: 32, alignSelf: 'center' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: alpha(stColor, 0.1), px: 2, py: 0.5, borderRadius: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stColor, boxShadow: `0 0 0 2px ${alpha(stColor, 0.3)}` }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DARK }}>{doc.statusName}</Typography>
                </Box>
                <Chip
                  label={doc.type === 5 ? 'ПЕРЕМЕЩЕНИЕ' : 'ВОЗВРАТ'} size="small"
                  sx={{ borderRadius: 1, fontWeight: 600, bgcolor: SOFT, border: `1px solid ${BORDER}`, color: 'text.secondary', fontSize: '0.7rem' }}
                />
                <MarkingChip marked={doc.marked} />
                <WatchBadge watch={doc.watch} />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {doc && extendVisible && (
              <Tooltip title={doc.canExtend ? '' : PERM_HINTS.extendOutOfZone}>
                <span>
                  <Button
                    size="small" variant="outlined" startIcon={<MoreTimeRoundedIcon />} disabled={!doc.canExtend}
                    onClick={() => setConfirm('extend')} sx={btnSx}
                  >
                    Продлить срок
                  </Button>
                </span>
              </Tooltip>
            )}
            {doc && actionItems.length > 0 && (
              <Button
                size="small" variant="outlined" color="inherit" startIcon={<MoreHorizRoundedIcon />}
                onClick={e => setActionsAnchor(e.currentTarget)} sx={{ ...btnSx, borderColor: BORDER, color: DARK }}
              >
                Действия
              </Button>
            )}
            {doc && access.exportMarks && (
              <Button
                size="small" variant="outlined" color="inherit" startIcon={<FileDownloadOutlinedIcon />}
                onClick={e => setExportAnchor(e.currentTarget)}
                sx={{ ...btnSx, borderColor: BORDER, color: DARK }}
              >
                {doc.marksCount > 0 ? `Выгрузка КМ'ов (${doc.marksCount})` : "Выгрузка КМ'ов"}
              </Button>
            )}
            <IconButton
              onClick={onClose} size="small" aria-label="Закрыть"
              sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: DANGER, color: 'white', borderColor: DANGER } }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>

        {loading && !doc && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}><CircularProgress /></Box>
        )}
        {error && !doc && <Alert severity="error" sx={{ m: 3, alignSelf: 'flex-start' }}>{error}</Alert>}

        {/* уже md - панели друг под другом с общей прокруткой, шире - у каждой панели своя */}
        {doc && (
          <Box sx={{ flexGrow: 1, display: 'flex', overflow: { xs: 'auto', md: 'hidden' }, minHeight: 0, flexDirection: { xs: 'column', md: 'row' } }}>
            <CardSidebar doc={doc} access={access} portalActions={data?.portalActions} retryVisible={retryVisible} onRetry={openRetry} />
            {/* key: у нового документа поиск по позициям начинается с пустой строки */}
            <CardItemsPanel key={doc.id} doc={doc} access={access} pricesAvailable={Boolean(data?.pricesAvailable)} />
          </Box>
        )}
      </Dialog>

      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)} slotProps={{ paper: { sx: { ...MENU_SX, minWidth: 240 } } }}>
        {actionItems.map(a => (
          <Tooltip key={a.kind} title={a.enabled ? '' : a.hint} placement="left">
            <span>
              <MenuItem disabled={!a.enabled} onClick={() => openConfirm(a.kind)} sx={a.danger ? { color: '#dc2626' } : undefined}>
                <ListItemIcon sx={{ color: 'inherit' }}>{a.icon}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{a.label}</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        ))}
      </Menu>

      <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)} slotProps={{ paper: { sx: { ...MENU_SX, minWidth: 220 } } }}>
        <MenuItem onClick={() => exportDocument('items', 'csv')}><ListItemText primaryTypographyProps={{ fontSize: 14 }}>Позиции - CSV</ListItemText></MenuItem>
        <MenuItem onClick={() => exportDocument('items', 'xlsx')}><ListItemText primaryTypographyProps={{ fontSize: 14 }}>Позиции - Excel</ListItemText></MenuItem>
        {canExportMarks && <Divider />}
        {canExportMarks && (
          <MenuItem onClick={() => exportDocument('marks', 'csv')}>
            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Коды маркировки - CSV</ListItemText>
          </MenuItem>
        )}
        {canExportMarks && (
          <MenuItem onClick={() => exportDocument('marks', 'xlsx')}>
            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Коды маркировки - Excel</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <CardConfirmDialog
        confirm={confirm} doc={doc} busy={busy} side={side} onSide={setSide}
        deleteAck={deleteAck} onDeleteAck={setDeleteAck} onCancel={cancelConfirm} onConfirm={runAction}
      />
    </>
  );
};

export default DocumentCardDialog;
